/**
 * /tools/wheel-of-life?clientId=X — STP-031.
 *
 * Step 3 6-axis self-assessment (1..10 per axis). Persists generic
 * tracker only — payload-in-notes covers it (no `wol_assessments` write
 * yet because the schema requires 8 axes incl. fun + environment that
 * the zod schema doesn't carry; R1.x adds them).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

const AXES = ['career', 'finance', 'health', 'family', 'romance', 'personalGrowth'] as const;
type Axis = (typeof AXES)[number];

type Scores = Record<Axis, number>;
type Notes = Partial<Record<Axis, string>>;

const DEFAULT_SCORES: Scores = {
  career: 0,
  finance: 0,
  health: 0,
  family: 0,
  romance: 0,
  personalGrowth: 0,
};

export function WheelOfLifePage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = useClientId();

  const [scores, setScores] = useState<Scores>(DEFAULT_SCORES);
  const [notes, setNotes] = useState<Notes>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'wheel-of-life');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const nextScores: Scores = { ...DEFAULT_SCORES };
          AXES.forEach((axis) => {
            if (typeof p[axis] === 'number') {
              nextScores[axis] = p[axis];
            }
          });
          const nextNotes: Notes = {};
          if (p.notes && typeof p.notes === 'object') {
            const np = p.notes as Record<string, unknown>;
            AXES.forEach((axis) => {
              if (typeof np[axis] === 'string') nextNotes[axis] = np[axis];
            });
          }
          setScores(nextScores);
          setNotes(nextNotes);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const allScored = useMemo(() => AXES.every((a) => scores[a] >= 1 && scores[a] <= 10), [scores]);
  const canSubmit = !submitting && hydrated && allScored;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!user) {
      setSubmitError(t('tools.wheelOfLife.errors.notAuthenticated'));
      return;
    }
    if (!clientId) {
      setSubmitError(t('tools.wheelOfLife.errors.missingClient'));
      return;
    }
    if (!allScored) {
      setSubmitError(t('tools.wheelOfLife.errors.incomplete'));
      return;
    }
    setSubmitting(true);
    const trimmedNotes: Notes = {};
    AXES.forEach((axis) => {
      const v = notes[axis]?.trim();
      if (v) trimmedNotes[axis] = v;
    });
    const payload: Record<string, unknown> = {
      ...scores,
      stepNumber: 3,
    };
    if (Object.keys(trimmedNotes).length > 0) {
      payload.notes = trimmedNotes;
    }
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'wheel-of-life',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.wheelOfLife.errors.generic'));
      } else {
        setSubmitError(t('tools.wheelOfLife.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="wheel-of-life-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.wheelOfLife.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.wheelOfLife.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2"><PrintButton /></div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.wheelOfLife.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.wheelOfLife.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.wheelOfLife.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {AXES.map((axis) => (
          <AxisCard
            key={axis}
            axis={axis}
            score={scores[axis]}
            note={notes[axis] ?? ''}
            onScoreChange={(v) => setScores((prev) => ({ ...prev, [axis]: v }))}
            onNoteChange={(v) => setNotes((prev) => ({ ...prev, [axis]: v }))}
          />
        ))}

        {submitError && (
          <p role="alert" className="form-error" data-testid="wheel-of-life-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="wheel-of-life-saved-at">
            ✓ {t('tools.wheelOfLife.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.wheelOfLife.submitting') : t('tools.wheelOfLife.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}

interface AxisCardProps {
  axis: Axis;
  score: number;
  note: string;
  onScoreChange: (value: number) => void;
  onNoteChange: (value: string) => void;
}

function AxisCard({ axis, score, note, onScoreChange, onNoteChange }: AxisCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <article
      className="rounded-lg border border-bone-3 bg-bone-2 p-s5"
      data-testid={`wheel-of-life-axis-${axis}`}
    >
      <h2 className="font-display text-display-sm text-ink">
        {t(`tools.wheelOfLife.axes.${axis}`)}
      </h2>
      <div
        role="radiogroup"
        aria-label={t(`tools.wheelOfLife.axes.${axis}`)}
        className="flex flex-wrap gap-s2 mt-s3"
        data-testid={`wheel-of-life-${axis}-scale`}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
          const active = n === score;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onScoreChange(n)}
              data-testid={`wheel-of-life-${axis}-scale-${n}`}
              className={
                active
                  ? 'h-9 w-9 rounded-full bg-flame text-bone font-medium'
                  : 'h-9 w-9 rounded-full border border-bone-3 text-gray-2 hover:border-flame hover:text-ink'
              }
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-gray-1 mt-s2">
        <span>{t('tools.wheelOfLife.scaleLabels.low')}</span>
        <span>{t('tools.wheelOfLife.scaleLabels.high')}</span>
      </div>
      <div className="mt-s3">
        <label
          htmlFor={`wheel-of-life-${axis}-note`}
          className="form-label"
        >
          {t('tools.wheelOfLife.fields.axisNotes.title')}
        </label>
        <textarea
          id={`wheel-of-life-${axis}-note`}
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder={t('tools.wheelOfLife.fields.axisNotes.placeholder')}
          className="input-field mt-s2 min-h-20"
          data-testid={`wheel-of-life-${axis}-note`}
          rows={2}
        />
      </div>
    </article>
  );
}
